(function () {
  'use strict';

  var INCH_TO_M = 0.0254;
  var FOOT_TO_M = 0.3048;
  var CM_TO_M = 0.01;
  var HOOP_BULLETPOINTS_SELECTOR = '[data-hoop-bulletpoints]';
  var HOOP_FACET_VALUE_SELECTOR = '[data-hoop-facet-value-label]';
  var HOOP_FACET_ACTIVE_SELECTOR = '[data-hoop-facet-active-value]';

  function roundTo(value, decimals) {
    var factor = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
  }

  function normalizeWhitespace(text) {
    return String(text || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function replaceAt(text, start, end, replacement) {
    return text.slice(0, start) + replacement + text.slice(end);
  }

  function formatMeters(valueMeters) {
    var safeValue = Number.isFinite(valueMeters) ? valueMeters : 0;
    var rounded = roundTo(Math.max(0, safeValue), 2);
    if (Math.abs(rounded) < 1e-9) return '0';
    return rounded.toFixed(2).replace(/\.?0+$/, '');
  }

  function formatInchesHalf(valueInches) {
    var safeValue = Number.isFinite(valueInches) ? valueInches : 0;
    var roundedHalf = roundTo(Math.max(0, safeValue) * 2, 0) / 2;
    var whole = Math.floor(roundedHalf);
    var fraction = roundedHalf - whole;

    if (Math.abs(fraction) < 1e-9) {
      return String(whole);
    }

    if (Math.abs(fraction - 0.5) < 1e-9) {
      return whole > 0 ? whole + ' 1/2' : '1/2';
    }

    return String(roundTo(roundedHalf, 1));
  }

  function findDimensionPair(text) {
    var normalized = String(text || '');
    var patterns = [
      {
        sourceUnit: 'in',
        regex: /(\d+(?:\.\d+)?)\s*(?:"|in(?:ch(?:es)?)?)\s*x\s*(\d+(?:\.\d+)?)\s*(?:"|in(?:ch(?:es)?)?)/i,
        toMeters: function (match) {
          return {
            first: Number(match[1]) * INCH_TO_M,
            second: Number(match[2]) * INCH_TO_M,
          };
        },
      },
      {
        sourceUnit: 'cm',
        regex: /(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*cm\b/i,
        toMeters: function (match) {
          return {
            first: Number(match[1]) * CM_TO_M,
            second: Number(match[2]) * CM_TO_M,
          };
        },
      },
      {
        sourceUnit: 'm',
        regex: /(\d+(?:\.\d+)?)\s*x\s*(\d+(?:\.\d+)?)\s*m\b/i,
        toMeters: function (match) {
          return {
            first: Number(match[1]),
            second: Number(match[2]),
          };
        },
      },
    ];

    for (var i = 0; i < patterns.length; i += 1) {
      var pattern = patterns[i];
      var match = pattern.regex.exec(normalized);
      if (!match) continue;
      var meters = pattern.toMeters(match);
      if (!meters || !Number.isFinite(meters.first) || !Number.isFinite(meters.second)) continue;
      return {
        sourceUnit: pattern.sourceUnit,
        start: match.index,
        end: match.index + match[0].length,
        firstMeters: meters.first,
        secondMeters: meters.second,
      };
    }

    return null;
  }

  function convertDimensionPair(text, targetUnit) {
    var pair = findDimensionPair(text);
    if (!pair) return text;

    if (targetUnit === 'imperial' && pair.sourceUnit === 'in') return text;
    if (targetUnit === 'metric' && pair.sourceUnit === 'm') return text;

    var replacement = '';
    if (targetUnit === 'imperial') {
      var firstInches = Math.round(pair.firstMeters / INCH_TO_M);
      var secondInches = Math.round(pair.secondMeters / INCH_TO_M);
      replacement = firstInches + '" x ' + secondInches + '"';
    } else {
      replacement = formatMeters(pair.firstMeters) + ' x ' + formatMeters(pair.secondMeters) + ' m';
    }

    return replaceAt(text, pair.start, pair.end, replacement);
  }

  function findFirstLengthToken(text) {
    var normalized = String(text || '');
    var patterns = [
      {
        sourceUnit: 'in',
        regex: /(\d+)\s+(\d+)\/(\d+)\s*(?:in|inch|inches|")/i,
        toMeters: function (match) {
          var whole = Number(match[1]);
          var numerator = Number(match[2]);
          var denominator = Number(match[3]);
          if (!Number.isFinite(whole) || !Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator === 0) {
            return NaN;
          }
          return (whole + numerator / denominator) * INCH_TO_M;
        },
      },
      {
        sourceUnit: 'in',
        regex: /(\d+(?:\.\d+)?)\s*(?:in|inch|inches|")/i,
        toMeters: function (match) {
          return Number(match[1]) * INCH_TO_M;
        },
      },
      {
        sourceUnit: 'ft',
        regex: /(\d+(?:\.\d+)?)\s*'/i,
        toMeters: function (match) {
          return Number(match[1]) * FOOT_TO_M;
        },
      },
      {
        sourceUnit: 'ft',
        regex: /(\d+(?:\.\d+)?)\s*(?:ft|feet)\b/i,
        toMeters: function (match) {
          return Number(match[1]) * FOOT_TO_M;
        },
      },
      {
        sourceUnit: 'cm',
        regex: /(\d+(?:\.\d+)?)\s*cm\b/i,
        toMeters: function (match) {
          return Number(match[1]) * CM_TO_M;
        },
      },
      {
        sourceUnit: 'm',
        regex: /(\d+(?:\.\d+)?)\s*m\b/i,
        toMeters: function (match) {
          return Number(match[1]);
        },
      },
    ];

    var winner = null;

    for (var i = 0; i < patterns.length; i += 1) {
      var pattern = patterns[i];
      var match = pattern.regex.exec(normalized);
      if (!match) continue;
      var meters = pattern.toMeters(match);
      if (!Number.isFinite(meters)) continue;

      var candidate = {
        sourceUnit: pattern.sourceUnit,
        start: match.index,
        end: match.index + match[0].length,
        meters: meters,
      };

      if (!winner || candidate.start < winner.start) {
        winner = candidate;
      }
    }

    return winner;
  }

  function convertSingleLength(text, targetUnit) {
    var token = findFirstLengthToken(text);
    if (!token) return text;

    if (targetUnit === 'imperial' && (token.sourceUnit === 'in' || token.sourceUnit === 'ft')) return text;
    if (targetUnit === 'metric' && token.sourceUnit === 'm') return text;

    var replacement = '';
    if (targetUnit === 'imperial') {
      var valueInches = token.meters / INCH_TO_M;
      replacement = formatInchesHalf(valueInches) + ' in';
    } else {
      replacement = formatMeters(token.meters) + ' m';
    }

    return replaceAt(text, token.start, token.end, replacement);
  }

  function convertBulletLine(text, targetUnit) {
    var normalized = normalizeWhitespace(text);
    if (!normalized) return normalized;
    if (targetUnit !== 'imperial' && targetUnit !== 'metric') return normalized;

    var converted = normalized;
    converted = convertDimensionPair(converted, targetUnit);

    if (/overhang/i.test(converted)) {
      converted = convertSingleLength(converted, targetUnit);
    }

    return normalizeWhitespace(converted);
  }

  function convertMeasurementText(text, targetUnit) {
    var normalized = normalizeWhitespace(text);
    if (!normalized) return normalized;
    if (targetUnit !== 'imperial' && targetUnit !== 'metric') return normalized;

    var converted = convertDimensionPair(normalized, targetUnit);
    converted = convertSingleLength(converted, targetUnit);

    return normalizeWhitespace(converted);
  }

  function resolveTargetUnit(countryCode, marketHandle) {
    var normalizedCountry = String(countryCode || '').toUpperCase();
    if (normalizedCountry) {
      return normalizedCountry === 'US' ? 'imperial' : 'metric';
    }

    var normalizedMarket = String(marketHandle || '').toLowerCase();
    if (!normalizedMarket) return null;

    if (normalizedMarket === 'us' || normalizedMarket === 'usa' || normalizedMarket === 'united-states') {
      return 'imperial';
    }

    return 'metric';
  }

  function isHoopCollectionPath(pathname) {
    return /\/collections\/[^?#]*basketball-hoops/i.test(String(pathname || ''));
  }

  function pageContainsHoopCards() {
    if (typeof document === 'undefined' || typeof document.querySelector !== 'function') return false;
    return Boolean(document.querySelector('[data-hoop-bulletpoints-is-hoop="true"]'));
  }

  function shouldProcessHoopMeasurements() {
    if (typeof window !== 'undefined' && window.location && isHoopCollectionPath(window.location.pathname)) {
      return true;
    }

    return pageContainsHoopCards();
  }

  function resolvePageTargetUnit() {
    var countryCode = '';
    var marketHandle = '';

    if (typeof window !== 'undefined' && window.Shopify) {
      countryCode = window.Shopify.country || '';
    }

    if (typeof document !== 'undefined' && typeof document.querySelector === 'function') {
      var hoopContainer = document.querySelector(HOOP_BULLETPOINTS_SELECTOR + '[data-hoop-bulletpoints-is-hoop="true"]');
      if (hoopContainer && hoopContainer.dataset) {
        if (!countryCode) countryCode = hoopContainer.dataset.hoopBulletpointsCountry || '';
        marketHandle = hoopContainer.dataset.hoopBulletpointsMarket || '';
      }
    }

    return resolveTargetUnit(countryCode, marketHandle);
  }

  function convertContainer(container) {
    if (!container || !container.dataset) return;
    if (container.dataset.hoopBulletpointsIsHoop !== 'true') return;

    var targetUnit = resolveTargetUnit(
      container.dataset.hoopBulletpointsCountry,
      container.dataset.hoopBulletpointsMarket
    );
    if (!targetUnit) return;
    if (container.dataset.hoopBulletpointsConverted === targetUnit) return;

    var items = container.querySelectorAll('li');
    if (!items.length) {
      container.dataset.hoopBulletpointsConverted = targetUnit;
      return;
    }

    for (var i = 0; i < items.length; i += 1) {
      var item = items[i];
      var currentText = normalizeWhitespace(item.textContent);
      if (!currentText) continue;
      var nextText = convertBulletLine(currentText, targetUnit);
      if (nextText && nextText !== currentText) {
        item.textContent = nextText;
      }
    }

    container.dataset.hoopBulletpointsConverted = targetUnit;
  }

  function convertFacetValueLabel(element, targetUnit) {
    if (!element) return;

    var originalText =
      element.dataset && element.dataset.originalLabel
        ? element.dataset.originalLabel
        : normalizeWhitespace(element.textContent);
    if (!originalText) return;

    var nextText = convertMeasurementText(originalText, targetUnit);
    if (nextText && nextText !== normalizeWhitespace(element.textContent)) {
      element.textContent = nextText;
    }
  }

  function convertActiveFacetValue(element, targetUnit) {
    if (!element) return;

    var filterLabel =
      element.dataset && element.dataset.originalFilterLabel
        ? normalizeWhitespace(element.dataset.originalFilterLabel)
        : '';
    var originalValueText =
      element.dataset && element.dataset.originalValueLabel
        ? element.dataset.originalValueLabel
        : normalizeWhitespace(element.textContent);
    if (!originalValueText) return;

    var convertedValue = convertMeasurementText(originalValueText, targetUnit);
    var nextText = filterLabel ? filterLabel + ': ' + convertedValue : convertedValue;
    if (nextText && nextText !== normalizeWhitespace(element.textContent)) {
      element.textContent = nextText;
    }
  }

  function processFacetRoot(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    if (!shouldProcessHoopMeasurements()) return;

    var targetUnit = resolvePageTargetUnit();
    if (!targetUnit) return;

    if (root.matches && root.matches(HOOP_FACET_VALUE_SELECTOR)) {
      convertFacetValueLabel(root, targetUnit);
    }

    if (root.matches && root.matches(HOOP_FACET_ACTIVE_SELECTOR)) {
      convertActiveFacetValue(root, targetUnit);
    }

    var valueLabels = root.querySelectorAll(HOOP_FACET_VALUE_SELECTOR);
    for (var i = 0; i < valueLabels.length; i += 1) {
      convertFacetValueLabel(valueLabels[i], targetUnit);
    }

    var activeValues = root.querySelectorAll(HOOP_FACET_ACTIVE_SELECTOR);
    for (var j = 0; j < activeValues.length; j += 1) {
      convertActiveFacetValue(activeValues[j], targetUnit);
    }
  }

  function processRoot(root) {
    if (!root || typeof root.querySelectorAll !== 'function') return;

    if (root.matches && root.matches(HOOP_BULLETPOINTS_SELECTOR)) {
      convertContainer(root);
    }

    var containers = root.querySelectorAll(HOOP_BULLETPOINTS_SELECTOR);
    for (var i = 0; i < containers.length; i += 1) {
      convertContainer(containers[i]);
    }

    processFacetRoot(root);
  }

  function observeNewContent() {
    if (typeof MutationObserver !== 'function') return;
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i += 1) {
        var mutation = mutations[i];
        if (!mutation.addedNodes || !mutation.addedNodes.length) continue;
        for (var j = 0; j < mutation.addedNodes.length; j += 1) {
          var node = mutation.addedNodes[j];
          if (!node || node.nodeType !== 1) continue;
          processRoot(node);
        }
      }
    });

    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  function init() {
    processRoot(document);
    observeNewContent();
  }

  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }
  }

  var api = {
    convertBulletLine: convertBulletLine,
    convertMeasurementText: convertMeasurementText,
    convertDimensionPair: convertDimensionPair,
    convertSingleLength: convertSingleLength,
    resolvePageTargetUnit: resolvePageTargetUnit,
    resolveTargetUnit: resolveTargetUnit,
    formatMeters: formatMeters,
    formatInchesHalf: formatInchesHalf,
    shouldProcessHoopMeasurements: shouldProcessHoopMeasurements,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (typeof window !== 'undefined') {
    window.SwishHoopBulletpointUnits = api;
  }
})();
